import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Clock } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabase/client';

interface CustomerServiceConfig {
  wechat_work_link: string;
  work_hours_start_morning: string;
  work_hours_end_morning: string;
  work_hours_start_afternoon: string;
  work_hours_end_afternoon: string;
  rest_time_tip: string;
  chat_button_text: string;
  customer_service_qr_code: string;
  customer_service_guide_text: string;
  customer_service_popup_title: string;
}

export default function FloatingChatButton() {
  const location = useLocation();
  const [config, setConfig] = useState<CustomerServiceConfig | null>(null);
  const [isWorkTime, setIsWorkTime] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // 只在首页显示
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    loadConfig();
    const timer = setInterval(checkWorkTime, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (config) checkWorkTime();
  }, [config]);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('*')
        .eq('id', 'default')
        .maybeSingle();
      if (error) throw error;
      if (data) setConfig(data as CustomerServiceConfig);
    } catch (err) {
      console.error('加载客服配置失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkWorkTime = () => {
    if (!config) return;
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const morningStart = parseTime(config.work_hours_start_morning);
    const morningEnd = parseTime(config.work_hours_end_morning);
    const afternoonStart = parseTime(config.work_hours_start_afternoon);
    const afternoonEnd = parseTime(config.work_hours_end_afternoon);
    const inMorning = currentTime >= morningStart && currentTime < morningEnd;
    const inAfternoon = currentTime >= afternoonStart && currentTime < afternoonEnd;
    setIsWorkTime(inMorning || inAfternoon);
  };

  const parseTime = (timeStr: string): number => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  };

  const handleClick = () => setShowModal(true);

  const formatWorkTime = () => {
    if (!config) return '';
    const morning = `${config.work_hours_start_morning}-${config.work_hours_end_morning}`;
    const afternoon = `${config.work_hours_start_afternoon}-${config.work_hours_end_afternoon}`;
    return `${morning}，${afternoon}`;
  };

  if (loading || !config || !isHomePage) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-[640px] right-6 z-50"
      >
        <AnimatePresence>
          {showTooltip && !isWorkTime && (
            <motion.div
              initial={{ opacity: 0, x: 10, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 10, scale: 0.9 }}
              className="absolute top-0 right-full mr-3 w-64 bg-white rounded-xl shadow-xl border border-blue-100 p-4"
            >
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 leading-relaxed">{config.rest_time_tip}</p>
              </div>
              <div className="absolute top-1/2 right-0 translate-x-1/2 rotate-45 w-3 h-3 bg-white border-r border-t border-blue-100" />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          whileHover={{ scale: 1.08, x: -6 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleClick}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="flex flex-col items-center gap-2 px-5 py-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl border-2 border-blue-400 shadow-xl shadow-blue-500/40 hover:from-blue-600 hover:to-blue-700 hover:shadow-2xl hover:shadow-blue-500/50 hover:border-blue-300 transition-all duration-300 min-w-[80px]"
        >
          <div className="relative">
            <MessageCircle className="w-9 h-9" strokeWidth={2} />
            {!isWorkTime && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full animate-pulse border-2 border-white" />
            )}
          </div>
          <span className="text-sm font-semibold whitespace-nowrap">{config.chat_button_text}</span>
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] bg-white rounded-2xl shadow-2xl z-50 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between">
                <h3 className="text-white font-semibold text-lg">{config.customer_service_popup_title || '联系客服'}</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                {config.customer_service_qr_code ? (
                  <div className="flex flex-col items-center mb-6">
                    <div className="w-48 h-48 bg-gray-50 rounded-xl border border-gray-200 p-3 flex items-center justify-center">
                      <img src={config.customer_service_qr_code} alt="客服微信二维码" className="w-full h-full object-contain" loading="lazy" />
                    </div>
                  </div>
                ) : (
                  <div className="w-48 h-48 mx-auto bg-gray-100 rounded-xl border border-gray-200 flex items-center justify-center mb-6">
                    <div className="text-center text-gray-400">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2" />
                      <span className="text-sm">暂无二维码</span>
                    </div>
                  </div>
                )}

                <p className="text-center text-gray-700 text-sm mb-4 leading-relaxed">
                  {config.customer_service_guide_text || '扫码添加客服微信，快速咨询考级/课程问题'}
                </p>

                <div className={`rounded-xl p-4 ${isWorkTime ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className={`w-4 h-4 ${isWorkTime ? 'text-green-600' : 'text-orange-600'}`} />
                    <span className={`font-medium text-sm ${isWorkTime ? 'text-green-700' : 'text-orange-700'}`}>
                      {isWorkTime ? '客服在线中' : '当前非工作时间'}
                    </span>
                  </div>
                  <p className={`text-sm ${isWorkTime ? 'text-green-600' : 'text-orange-600'}`}>服务时间：{formatWorkTime()}</p>
                  {!isWorkTime && <p className="text-sm text-orange-600 mt-2">{config.rest_time_tip}</p>}
                </div>

                <div className="mt-6">
                  <button onClick={() => setShowModal(false)} className="w-full py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors">关闭</button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
